// PLAT-3418 - SENDER PRESENTATION. Top-level keys on `chat.meta`:
//
//   meta.senderLabel  String   "Meta AI" | "Bot" | "Agent" | anything
//   meta.senderIcon   String   a NAME from src/declaredIcons.js, never a URL
//   meta.aiGenerated  Boolean  the chat's CONTENT was produced by an AI
//
// NOT an AI feature: a human agent uses the same slot, so `[Agent]`, `[Bot]` and
// `[Meta AI]` are one mechanism.
//
// ONE SOURCE FOR THE LABEL. The bubble (here) and the thread-list preview
// (inbox-integration-api `util/senderTag.js`) MUST both resolve from
// `meta.senderLabel` and nothing else, or a chat reads "Meta AI" on the bubble
// and "[Bot]" in the list.
//
// PLAT-3415: `chat.meta` is a JSON field, so a selection set cannot prune it.
// Everything in it reaches every inbox session that can see the chat. Render
// hints only - no tokens, ids, prompts or model names.
//
// `aiGenerated` HAS NO RENDERER, BY DESIGN. Ruled by Roy 2026-08-31 GMT+8, "keep
// it for non-visual use" (AI-disclosure marking), the same day he removed its
// only renderer. Do not delete it as dead code, and keep the `Chat.meta`
// docstring in inbox-integration-api in step - that is where an extension author
// reads that the key intentionally does nothing today.

/** Also appears in the thread list, so capped shorter than a mode label. */
export const MAX_SENDER_LABEL_LENGTH = 18

export function capSenderLabel(label) {
  if (typeof label !== "string") {
    return ""
  }
  const trimmed = label.trim()
  if (trimmed.length <= MAX_SENDER_LABEL_LENGTH) {
    return trimmed
  }
  return `${trimmed.slice(0, MAX_SENDER_LABEL_LENGTH - 1)}…`
}

/**
 * What to render for this chat's sender, or nulls when the writer said nothing.
 *
 * `chat.meta` is extension-authored and untrusted, so every field is coerced
 * rather than used: a non-string label becomes empty, not "[object Object]".
 *
 * `hasSender` MUST NOT depend on `aiGenerated`. A chat with
 * `senderLabel: "Agent"` and no `aiGenerated` renders the full row - that is the
 * human-agent case, and gating on `aiGenerated` would make it unbuildable.
 */
export function senderPresentation(chat) {
  const meta = (chat && chat.meta) || {}
  const label = capSenderLabel(meta.senderLabel)
  return {
    label,
    hasSender: label.length > 0,
    iconName: typeof meta.senderIcon === "string" ? meta.senderIcon : null,
    // Strictly true, not truthy: a stray "false" string must not flag a chat.
    aiGenerated: meta.aiGenerated === true,
  }
}

/**
 * A declared sender's icon is SMALLER than the default robot's, deliberately.
 *
 * Ruled by Roy 2026-09-01 GMT+8, "apply the size to declared senders only": a
 * channel writing none of these keys MUST render bit-for-bit as before, so size
 * and colour both branch on `hasSender`. The two sizes differing looks like an
 * inconsistency and is not one.
 *
 * Lives here rather than in Bot.js so the scope is testable - Bot.js has no
 * tests, and it reads this for BOTH the icon and its box, which MUST agree.
 */
export const DECLARED_SENDER_ICON_SIZE = "1.44em"

/** Reproduces the old FontAwesome `size="XS"` box. Keeps existing bubbles identical. */
export const DEFAULT_SENDER_ICON_SIZE = "24px"

export function senderIconSize(hasSender) {
  return hasSender ? DECLARED_SENDER_ICON_SIZE : DEFAULT_SENDER_ICON_SIZE
}
